import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, ChevronLeft, ChevronRight, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WT = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
const MONATE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const parse = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
const montag = (s) => { const d = parse(s); d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); return d; };
const plusTage = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const kw = (d) => {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  t.setUTCDate(t.getUTCDate() + 4 - (t.getUTCDay() || 7));
  return Math.ceil(((t - new Date(Date.UTC(t.getUTCFullYear(), 0, 1))) / 86400000 + 1) / 7);
};
const kurz = (s) => { const d = parse(s); return `${WT[d.getDay()]} ${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.`; };

// Tägliche Klassenbuch-Pflicht gilt ab diesem Tag; ältere Tage lösen keine Erinnerung aus
const KLASSENBUCH_PFLICHT_AB = '2026-10-15';

const STATUS = {
  abgeschlossen: { label: 'abgeschlossen', cls: 'bg-[#E2621B] text-white border-[#E2621B]' },
  gespeichert: { label: 'nicht abgeschlossen', cls: 'bg-amber-50 text-amber-800 border-amber-300' },
  offen: { label: 'offen', cls: 'bg-stone-900 text-white border-stone-900' },
  geplant: { label: 'geplant', cls: 'bg-white text-stone-800 border-stone-800' },
  feiertag: { label: 'Feiertag', cls: 'bg-stone-100 text-stone-500 border-stone-300' },
  kein_unterricht: { label: 'kein Unterricht', cls: 'bg-stone-100 text-stone-500 border-stone-200' },
};

const kurseintrag = (t, kursId) => {
  const k = (t.kurse || []).find((x) => x.kurs_id === kursId);
  const start = (k?.start_datum || k?.start || t.kurs_start || '').substring(0, 10);
  const ende = (k?.end_datum || k?.ende || t.kurs_ende || '').substring(0, 10);
  return { start, ende };
};

export const kohorteFuer = (teilnehmer, kursId, datum) => {
  const zaehl = {};
  teilnehmer.forEach((t) => {
    const { start, ende } = kurseintrag(t, kursId);
    if (!start || start > datum || (ende && ende < datum)) return;
    const key = `${start}|${ende}`;
    zaehl[key] = (zaehl[key] || 0) + 1;
  });
  const best = Object.entries(zaehl).sort((a, b) => b[1] - a[1])[0];
  if (!best) return null;
  const [start, ende] = best[0].split('|');
  return { start, ende };
};

const ladePlan = async (kursId, von, bis, kohorte) => {
  const { data } = await axios.get(`${API}/anwesenheit-tag/plan/${kursId}`, {
    params: { von, bis, kurs_start: kohorte?.start || '', kurs_ende: kohorte?.ende || '' },
  });
  return data.tage || [];
};

export const OffeneTageHinweis = ({ kursId, kohorte, reloadKey, onSelectDay }) => {
  const [offen, setOffen] = useState([]);
  useEffect(() => {
    if (!kohorte?.start) { setOffen([]); return; }
    const gestern = iso(plusTage(new Date(), -1));
    const bis = kohorte.ende && kohorte.ende < gestern ? kohorte.ende : gestern;
    const von = kohorte.start > KLASSENBUCH_PFLICHT_AB ? kohorte.start : KLASSENBUCH_PFLICHT_AB;
    if (bis < von) { setOffen([]); return; }
    ladePlan(kursId, von, bis, kohorte)
      .then((tage) => setOffen(tage.filter((t) => ['offen', 'gespeichert'].includes(t.status)).reverse()))
      .catch(() => setOffen([]));
  }, [kursId, kohorte?.start, kohorte?.ende, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!offen.length) return null;
  return (
    <div data-testid="offene-tage-hinweis" className="mx-4 mt-4 p-3 rounded-xl border border-[#F8D6C3] bg-[#FEF4EE]">
      <div className="flex items-center gap-2 text-sm font-semibold text-[#9A3412]">
        <AlertTriangle className="h-4 w-4" />
        {offen.length} vergangene{offen.length === 1 ? 'r' : ''} Unterrichtstag{offen.length === 1 ? '' : 'e'} noch nicht abgeschlossen
      </div>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {offen.slice(0, 12).map((t) => (
          <button key={t.datum} onClick={() => onSelectDay(t.datum)} data-testid={`offener-tag-${t.datum}`}
            className="px-2.5 py-1 text-xs font-medium rounded-full bg-white border border-[#F8D6C3] text-[#9A3412] hover:bg-[#E2621B] hover:text-white transition-colors">
            {kurz(t.datum)}
          </button>
        ))}
        {offen.length > 12 && <span className="text-xs text-[#9A3412] self-center">+{offen.length - 12} weitere</span>}
      </div>
    </div>
  );
};

const WochenZeile = ({ t, aktiv, onSelect }) => {
  const st = STATUS[t.status] || STATUS.offen;
  const thema = t.themen_ist.length ? t.themen_ist.join(' · ') : t.thema_plan;
  const geaendert = t.themen_ist.length > 0 && t.thema_plan && !t.themen_ist.includes(t.thema_plan);
  return (
    <tr onClick={() => onSelect(t.datum)} data-testid={`woche-tag-${t.datum}`}
      className={`border-b border-[#ECEAE6] cursor-pointer transition-colors ${aktiv ? 'bg-[#FEF4EE]' : 'hover:bg-[#F7F6F4]'}`}>
      <td className="py-3 pr-3 font-semibold text-stone-900 whitespace-nowrap">{kurz(t.datum)}</td>
      <td className="py-3 pr-3 text-stone-600 whitespace-nowrap">{t.status === 'feiertag' || t.status === 'kein_unterricht' ? '–' : t.zeit}</td>
      <td className="py-3 pr-3 text-stone-600">{t.ue ?? '–'}</td>
      <td className="py-3 pr-3 text-stone-900 font-medium">
        {t.feiertag || thema || <span className="text-stone-400 font-normal">kein Thema hinterlegt</span>}
        {t.tag_nr && <span className="ml-2 text-xs text-stone-400 font-normal">Tag {t.tag_nr}</span>}
        {geaendert && <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">geändert</span>}
      </td>
      <td className="py-3 pr-3 text-stone-600 whitespace-nowrap">{t.dozent || '–'}</td>
      <td className="py-3 pr-3 text-stone-600 whitespace-nowrap">{t.gesamt ? `${t.anwesend} / ${t.gesamt}` : '–'}</td>
      <td className="py-3"><span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${st.cls}`}>{st.label}</span></td>
    </tr>
  );
};

export const KlassenbuchWoche = ({ kursId, datum, kohorte, reloadKey, onSelectDay }) => {
  const [woche, setWoche] = useState(() => montag(datum));
  const [tage, setTage] = useState([]);
  useEffect(() => { setWoche(montag(datum)); }, [datum]);
  useEffect(() => {
    ladePlan(kursId, iso(woche), iso(plusTage(woche, 4)), kohorte).then(setTage).catch(() => setTage([]));
  }, [kursId, woche, kohorte?.start, kohorte?.ende, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const fr = plusTage(woche, 4);
  const titel = useMemo(() => `KW ${kw(woche)} · ${woche.getDate()}.${woche.getMonth() !== fr.getMonth() ? ` ${MONATE[woche.getMonth()]}` : ''} bis ${fr.getDate()}. ${MONATE[fr.getMonth()]}`, [woche]); // eslint-disable-line react-hooks/exhaustive-deps
  const fertig = tage.filter((t) => t.status === 'abgeschlossen').length;

  return (
    <div className="mx-4 mt-4 p-5 rounded-2xl border border-[#ECEAE6] bg-white" data-testid="klassenbuch-woche" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <h3 className="text-lg font-bold tracking-tight text-stone-900" data-testid="woche-titel">{titel}</h3>
          <div className="flex items-center gap-2 mt-1 text-xs">
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#E2621B] text-white font-semibold">
              <CheckCircle2 className="h-3 w-3" /> vorbefüllt aus Stundenplan
            </span>
            <span className="text-stone-500">{fertig} von {tage.filter((t) => !['feiertag', 'kein_unterricht'].includes(t.status)).length} Tagen abgeschlossen</span>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setWoche(plusTage(woche, -7))} data-testid="woche-zurueck"
            className="h-9 w-9 rounded-full border border-stone-800 flex items-center justify-center hover:bg-stone-900 hover:text-white transition-colors"><ChevronLeft className="h-4 w-4" /></button>
          <button onClick={() => setWoche(plusTage(woche, 7))} data-testid="woche-vor"
            className="h-9 w-9 rounded-full border border-stone-800 flex items-center justify-center hover:bg-stone-900 hover:text-white transition-colors"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-semibold text-stone-500 border-b-2 border-stone-900">
              <th className="pb-2 pr-3">Datum</th><th className="pb-2 pr-3">Zeit</th><th className="pb-2 pr-3">UE</th>
              <th className="pb-2 pr-3">Thema laut Stundenplan</th><th className="pb-2 pr-3">Dozent/in</th>
              <th className="pb-2 pr-3">Anwesend</th><th className="pb-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {tage.map((t) => <WochenZeile key={t.datum} t={t} aktiv={t.datum === datum} onSelect={onSelectDay} />)}
          </tbody>
        </table>
      </div>
      {!kohorte && <p className="text-xs text-stone-500 mt-2">Kein Kursbeginn bei den Teilnehmenden hinterlegt – Themen werden nur angezeigt, wenn sie im Klassenbuch eingetragen sind.</p>}
    </div>
  );
};
